import { useEffect, useRef, useState } from 'react';
import { useOpenApiClient } from './base/useOpenApiClient';
import { ErrorResponse } from 'interfaces/baseTypes';
import { components } from './base/schema';

type SalesChannelResponse = components['schemas']['SalesChannelResponse'];

const useSalesChannelOpenApi = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<ErrorResponse | null>(null);
    const {client, getClient} = useOpenApiClient();
    const clientRef = useRef(client);

    useEffect(() => {
        clientRef.current = client;
    }, [client]);

    async function getSalesChannels(): Promise<SalesChannelResponse[]> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/channels');

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return [];
            }

            return response?.data as SalesChannelResponse[];
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return [];
        } finally {
            setLoading(false);
        }
    }

    async function getSalesChannelById(salesChannelId: number): Promise<SalesChannelResponse | null> {
        setLoading(true);
        setErrorMessage(null);
        try {
            if(!clientRef.current){
                clientRef.current = getClient();
            }
            
            if (!clientRef.current) {
                throw new Error('API client is not initialized');
            }
            
            const response = await clientRef.current.GET('/api/channels/{salesChannelId}', {
                params: { path: { salesChannelId } },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return null;
            }

            return response?.data as SalesChannelResponse;
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return null;
        } finally {
            setLoading(false);
        }
    }

    return { getSalesChannels, getSalesChannelById, loading, errorMessage };
}

export default useSalesChannelOpenApi;
